import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi, clearAdminToken, getAdminToken, PROFILE_LABELS, PROFILE_BADGE } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState("all");
  const [q, setQ] = useState("");
  const [stats, setStats] = useState({ total: 0, by_profile: {} });
  const [settings, setSettings] = useState({ booking_url: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(loadList, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, q]);

  async function loadAll() {
    setLoading(true);
    try {
      const [list, st, set] = await Promise.all([
        adminApi.get("/admin/diagnostics", { params: { profile, q } }),
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/settings"),
      ]);
      setItems(list.data.items || []);
      setStats(st.data);
      setSettings({ booking_url: set.data.booking_url || "" });
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        navigate("/admin");
      } else {
        toast.error("Erreur de chargement.");
      }
    }
    setLoading(false);
  }

  async function loadList() {
    try {
      const list = await adminApi.get("/admin/diagnostics", { params: { profile, q } });
      setItems(list.data.items || []);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        navigate("/admin");
      }
    }
  }

  async function saveSettings(e) {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await adminApi.put("/admin/settings", { booking_url: settings.booking_url });
      toast.success("Lien Google Calendar mis à jour.");
    } catch {
      toast.error("Impossible d'enregistrer.");
    }
    setSavingSettings(false);
  }

  function logout() {
    clearAdminToken();
    navigate("/admin");
  }

  function fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  }

  return (
    <div className="min-h-screen bg-sand-50" data-testid="admin-dashboard-page">
      <header className="border-b border-sand-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.wixstatic.com/media/af9b25_f36f1ab2bff848758e1745acea6e006c~mv2.png/v1/fill/w_320,h_188,al_c,q_85,enc_avif,quality_auto/eclaire_toi_texte_et_main.png"
              alt="Éclaire-Toi"
              className="h-12 w-auto"
            />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.28em] text-sand-500">Espace admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-ink-soft hover:text-ink">Voir le site</Link>
            <button
              onClick={logout}
              className="text-sm rounded-full border border-sand-200 px-4 py-2 hover:bg-sand-100 transition"
              data-testid="logout-btn"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="stats-section">
          <div className="rounded-2xl bg-white border border-sand-200 p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-ink-muted">Total</p>
            <p className="mt-2 font-serif text-3xl text-ink">{stats.total}</p>
          </div>
          {Object.entries(PROFILE_LABELS).map(([key, label]) => (
            <div key={key} className="rounded-2xl bg-white border border-sand-200 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-ink-muted">{label}</p>
              <p className="mt-2 font-serif text-3xl text-ink">{stats.by_profile?.[key] || 0}</p>
            </div>
          ))}
        </section>

        {/* Settings */}
        <section className="mt-8 rounded-2xl bg-white border border-sand-200 p-6">
          <h2 className="font-serif text-xl text-ink">Lien Google Calendar pour la réservation</h2>
          <p className="text-sm text-ink-soft mt-1">
            Ce lien est utilisé après la soumission du formulaire de contact pour rediriger vers votre planning.
          </p>
          <form onSubmit={saveSettings} className="mt-4 flex flex-col sm:flex-row gap-3">
            <input
              type="url"
              value={settings.booking_url}
              onChange={(e) => setSettings((s) => ({ ...s, booking_url: e.target.value }))}
              placeholder="https://calendar.google.com/calendar/u/0/appointments/..."
              className="flex-1 rounded-xl border border-sand-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sand-300 focus:border-sand-500 transition"
              data-testid="booking-url-input"
            />
            <button
              type="submit"
              disabled={savingSettings}
              className="group inline-flex items-center gap-2 rounded-full bg-sand-500 hover:bg-sand-600 text-white pl-5 pr-2 py-2 shadow-soft transition-all duration-500 hover:shadow-elevated disabled:opacity-50 whitespace-nowrap"
              data-testid="save-booking-url-btn"
            >
              <span className="text-sm font-medium tracking-wide leading-none">
                {savingSettings ? "…" : "Enregistrer"}
              </span>
              <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs transition-all duration-500 group-hover:bg-white group-hover:text-sand-500">
                ✓
              </span>
            </button>
          </form>
        </section>

        {/* Filters */}
        <section className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par prénom, nom, email ou téléphone…"
            className="flex-1 rounded-xl border border-sand-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sand-300 focus:border-sand-500 transition"
            data-testid="search-input"
          />
          <div className="sm:w-64">
            <Select value={profile} onValueChange={setProfile}>
              <SelectTrigger data-testid="profile-filter" className="bg-white border-sand-200 h-12 rounded-xl">
                <SelectValue placeholder="Tous les profils" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les profils</SelectItem>
                {Object.entries(PROFILE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Table */}
        <section className="mt-6 rounded-2xl bg-white border border-sand-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-ink-soft animate-breath">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-ink-soft">
              Aucun diagnostic ne correspond à votre recherche.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="diagnostics-table">
                <thead className="bg-sand-100/60 text-ink-soft uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-4">Date</th>
                    <th className="text-left px-5 py-4">Prénom</th>
                    <th className="text-left px-5 py-4">Nom</th>
                    <th className="text-left px-5 py-4">Email</th>
                    <th className="text-left px-5 py-4">Téléphone</th>
                    <th className="text-left px-5 py-4">Profil dominant</th>
                    <th className="text-right px-5 py-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const c = it.contact || {};
                    const dominants = it.profile_dominants || [it.profile_primary];
                    return (
                      <tr key={it.id} className="border-t border-sand-100 hover:bg-sand-50/60 transition">
                        <td className="px-5 py-4 text-ink-soft whitespace-nowrap">{fmtDate(it.created_at)}</td>
                        <td className="px-5 py-4 text-ink">{c.first_name || <span className="text-ink-muted">·</span>}</td>
                        <td className="px-5 py-4 text-ink">{c.last_name || <span className="text-ink-muted">·</span>}</td>
                        <td className="px-5 py-4 text-ink-soft">{c.email || <span className="text-ink-muted">·</span>}</td>
                        <td className="px-5 py-4 text-ink-soft">{c.phone || <span className="text-ink-muted">·</span>}</td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {dominants.map((d) => (
                              <span
                                key={d}
                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${PROFILE_BADGE[d]}`}
                              >
                                {PROFILE_LABELS[d]}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <Link
                            to={`/admin/diagnostics/${it.id}`}
                            className="text-sand-600 hover:text-sand-700 font-medium"
                            data-testid={`view-diagnostic-${it.id}`}
                          >
                            Voir →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
